import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Lottie } from "lottie-react";
import { useTheme } from "./useTheme";
import { useOfflineStatus, formatCacheTime } from "./useOfflineStatus";
import wifiOfflineAnim from "./wifi_offline.json";

const SHOW_DELAY_MS = 3000;
const HIDE_DELAY_MS = 300;

export default function ConnectionIssueModal() {
  const { isOnline, cacheTs, cacheUsed } = useOfflineStatus();
  const { isDark } = useTheme();
  const problem = !isOnline && !cacheUsed;

  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [retryBusy, setRetryBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    if (!problem) setDismissed(false);
  }, [problem]);

  useEffect(() => {
    clearTimeout(showTimerRef.current);
    clearTimeout(hideTimerRef.current);
    const shouldShow = problem && !dismissed;
    if (shouldShow) {
      showTimerRef.current = setTimeout(() => {
        setMounted(true);
        requestAnimationFrame(() => setVisible(true));
      }, SHOW_DELAY_MS);
    } else {
      setVisible(false);
      hideTimerRef.current = setTimeout(() => setMounted(false), HIDE_DELAY_MS);
    }
    return () => {
      clearTimeout(showTimerRef.current);
      clearTimeout(hideTimerRef.current);
    };
  }, [problem, dismissed]);

  const handleRetry = async () => {
    if (retryBusy) return;
    setRetryBusy(true);
    try {
      await fetch("/auth/online-count", { cache: "no-store" });
    } catch {}
    setTimeout(() => setRetryBusy(false), 900);
  };

  if (!mounted) return null;

  const title = "Нет подключения к интернету";
  const subtitle = "Проверьте Wi-Fi или мобильный интернет. Как только связь появится, окно закроется само.";

  const bg = isDark ? "rgba(0,0,0,0.78)" : "rgba(15,23,42,0.55)";
  const card = isDark ? "#1c1c1e" : "#ffffff";
  const cardBorder = isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.06)";
  const textPrimary = isDark ? "#f5f5f7" : "#0f172a";
  const textSecondary = isDark ? "rgba(235,235,245,0.62)" : "#64748b";
  const btnPrimaryBg = isDark ? "#0a84ff" : "#2563eb";
  const btnPrimaryHover = isDark ? "#2a94ff" : "#1d4ed8";
  const ghostText = isDark ? "rgba(235,235,245,0.7)" : "#475569";
  const chipBg = isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)";

  return createPortal(
    <div
      aria-live="polite"
      role="alertdialog"
      className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center p-4"
      style={{
        background: bg,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        opacity: visible ? 1 : 0,
        transition: `opacity ${HIDE_DELAY_MS}ms ease-out`,
      }}
    >
      <div
        className="relative w-full max-w-[380px] rounded-[28px] overflow-hidden"
        style={{
          background: card,
          border: cardBorder,
          boxShadow: isDark
            ? "0 25px 60px -12px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.04)"
            : "0 25px 60px -12px rgba(15,23,42,0.35)",
          transform: visible ? "translateY(0) scale(1)" : "translateY(16px) scale(0.96)",
          opacity: visible ? 1 : 0,
          transition: `transform 260ms cubic-bezier(0.2,0.9,0.2,1), opacity ${HIDE_DELAY_MS}ms ease-out`,
        }}
      >
        <button
          type="button"
          aria-label="Скрыть"
          onClick={() => setDismissed(true)}
          className="absolute right-3 top-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors ci-close"
          style={{ color: textSecondary, zIndex: 1 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="relative px-7 pt-6 pb-5 flex flex-col items-center text-center">
          <div style={{ width: 140, height: 140, marginBottom: 8, flexShrink: 0 }}>
            <Lottie src={wifiOfflineAnim} loop autoplay style={{ width: 140, height: 140 }} />
          </div>

          <h3 className="text-[18px] font-semibold leading-tight" style={{ color: textPrimary }}>
            {title}
          </h3>
          <p className="mt-2 text-[13.5px] leading-snug max-w-[300px]" style={{ color: textSecondary }}>
            {subtitle}
          </p>

          {cacheUsed && (
            <div
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium"
              style={{ background: chipBg, color: textSecondary }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Показаны {cacheTs ? `данные от ${formatCacheTime(cacheTs)}` : "сохранённые данные"}
            </div>
          )}
        </div>

        <div className="px-7 pb-7 flex flex-col gap-2">
          <button
            type="button"
            onClick={handleRetry}
            disabled={retryBusy}
            className="w-full h-12 rounded-2xl text-[15px] font-semibold text-white transition-all duration-150 ci-primary"
            style={{ background: btnPrimaryBg }}
          >
            {retryBusy ? (
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                Проверяем…
              </span>
            ) : "Повторить"}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full h-11 rounded-2xl text-[14px] font-medium transition-colors duration-150 ci-ghost"
            style={{ color: ghostText }}
          >
            Перезагрузить страницу
          </button>
        </div>
      </div>

      <style>{`
        .ci-primary:hover:not(:disabled) { background: ${btnPrimaryHover} !important; }
        .ci-primary:active:not(:disabled) { transform: scale(0.98); }
        .ci-primary:disabled { opacity: 0.75; cursor: default; }
        .ci-ghost:hover { background: ${isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)"}; }
        .ci-ghost:active { background: ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}; }
        .ci-close:hover { background: ${isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)"}; color: ${textPrimary}; }
        .ci-close:active { background: ${isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.08)"}; }
      `}</style>
    </div>,
    document.body
  );
}
